import { memo } from "react";
import { Handle, Position } from "@xyflow/react";
import {
  Bot,
  Braces,
  CircleHelp,
  Clock,
  Flag,
  Image,
  MessageSquare,
  Split,
  Tag as TagIcon,
  UserPlus,
  Webhook,
  Workflow,
} from "lucide-react";
import { BRANCH_HANDLE, NEXT_HANDLE } from "./flowGraph";

const ICONS = {
  message: MessageSquare,
  image: Image,
  help: CircleHelp,
  branch: Split,
  clock: Clock,
  api: Braces,
  sparkles: Bot,
  webhook: Webhook,
  tag: TagIcon,
  funnel: Workflow,
  user: UserPlus,
  handoff: UserPlus,
  stop: Flag,
};

const GROUP = {
  conversa: { bar: "bg-green-500", ring: "ring-green-500" },
  logica: { bar: "bg-amber-500", ring: "ring-amber-500" },
  integracao: { bar: "bg-blue-500", ring: "ring-blue-500" },
  crm: { bar: "bg-purple-500", ring: "ring-purple-500" },
};

/** Resumo do que o nó faz, para a caixa dizer algo sem precisar abri-la. */
function summarize(node) {
  const text = node.text || node.caption || node.prompt || node.url || node.stage;
  if (node.type === "wait") return `${node.minutes || 0} min`;
  if (node.type === "http") return `${node.method || "GET"} ${node.url || ""}`;
  if (!text) return null;
  return String(text).length > 70 ? `${String(text).slice(0, 70)}…` : String(text);
}

const HANDLE_CLASS = "!h-3 !w-3 !border-2 !border-white !bg-gray-400 dark:!border-gray-900";

const ScriptNode = ({ data, selected }) => {
  const { node, spec, isStart } = data;
  const Icon = ICONS[spec?.icon] || MessageSquare;
  const colors = GROUP[spec?.group] || { bar: "bg-gray-400", ring: "ring-gray-400" };
  const branches = node.branches || [];
  const summary = summarize(node);

  return (
    <div
      className={`w-60 overflow-hidden rounded-lg border bg-white shadow-sm transition-shadow dark:bg-gray-900 ${
        selected
          ? `border-transparent ring-2 ${colors.ring}`
          : "border-gray-200 dark:border-gray-700"
      }`}
    >
      <Handle type="target" position={Position.Top} className={HANDLE_CLASS} />

      <div className={`h-1 w-full ${colors.bar}`} />

      <div className="p-2.5">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 shrink-0 text-gray-500" />
          <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
            {node.label || spec?.label || node.type}
          </span>
          {isStart && (
            <span className="ml-auto shrink-0 rounded-full bg-green-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-green-800 dark:bg-green-900/40 dark:text-green-300">
              início
            </span>
          )}
        </div>

        <p className="mt-1 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
          {summary || spec?.label}
        </p>

        {branches.length > 0 && (
          <div className="mt-2 space-y-1 border-t border-gray-100 pt-1.5 dark:border-gray-800">
            {branches.map((branch, index) => (
              <div
                key={index}
                className="flex items-center justify-between gap-1 text-[10px] text-gray-600 dark:text-gray-400"
              >
                <span className="truncate">
                  {branch.when || "contém"} {branch.value || "…"}
                </span>
                <span className="shrink-0 text-gray-400">→</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Uma saída por caminho: é o que permite ligar cada condição a um destino
          diferente arrastando, como no editor de fluxo do n8n. */}
      {!spec?.terminal && (
        <>
          {branches.map((_, index) => (
            <Handle
              key={index}
              id={BRANCH_HANDLE(index)}
              type="source"
              position={Position.Right}
              className={HANDLE_CLASS}
              style={{ top: 60 + index * 18 }}
            />
          ))}

          <Handle
            id={NEXT_HANDLE}
            type="source"
            position={Position.Bottom}
            className={HANDLE_CLASS}
          />
        </>
      )}
    </div>
  );
};

export default memo(ScriptNode);
